/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

// Lib
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';

// Store
import useStore from '@store/index';

// Types
import {
	TApiResponseCreateApiToken,
	TBodyCreateApiToken,
} from '../types';
import { queryKeys } from '@config/constants/query-keys';
import { TApiError } from '@global-types/api/api';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const addApiToken = (
	body: TBodyCreateApiToken
): Promise<TApiResponseCreateApiToken> => {
	return apiClient.post('/api_tokens', body);
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useAddApiToken = (
	page: number,
	size: number
) => {
	const { setApiTokensLastAccessTokenCreated } =
		useStore();

	const { t } = useTranslation();

	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: addApiToken,
		onSuccess: async (response) => {
			if (response) {
				const { value } = response;
				setApiTokensLastAccessTokenCreated(value);
			}

			notifications.show({
				title: t('global.success'),
				message: t(
					'apiTokens.notifications.apiTokenSuccessfullyAdded'
				),
				color: 'success',
			});

			queryClient.invalidateQueries({
				queryKey: [
					queryKeys.app.apiTokens.list.key,
					{ page: page, size: size },
				],
			});
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'error',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
